<!-- Единая форма создания и редактирования транспорта. -->
<template>
  <form
    :id="formId"
    class="vehicle-form"
    @submit.prevent="handleSubmit"
  >
    <!-- Основные поля ТС должны совпадать в создании, быстром создании и карточке. -->
    <div class="vehicle-form__block">
      <BaseFormSection title="Основная информация">
        <BaseFormField
          label="Автопарк"
          for-id="vehicle_fleet_park_id"
        >
          <FleetParkSelect
            id="vehicle_fleet_park_id"
            v-model="formData.fleet_park_id"
          />
        </BaseFormField>

        <BaseFormField
          label="Тип ТС"
          for-id="vehicle_type"
          required
          :filled="Boolean(formData.vehicle_type)"
          :error="errors.vehicle_type"
        >
          <base-select
            id="vehicle_type"
            v-model="formData.vehicle_type"
            :options="vehicleStore.vehicleTypeOptions"
            placeholder="Выберите тип ТС"
            :invalid="errors.vehicle_type"
            @update:modelValue="handleVehicleTypeInput"
          />
        </BaseFormField>

        <BaseFormField
          label="Регистрационный номер"
          for-id="vehicle_registration_number"
          required
          :filled="Boolean(formData.registration_number)"
          :error="errors.registration_number"
          info-key="vehicle.common"
        >
          <base-input
            id="vehicle_registration_number"
            v-model="formData.registration_number"
            type="text"
            class="form-input"
            :invalid="errors.registration_number"
            @update:modelValue="errors.registration_number = false"
          />
        </BaseFormField>

        <BaseFormField
          v-if="formData.vehicle_type === 'trailer'"
          label="Тип прицепа"
          for-id="vehicle_trailer_type"
          info-key="vehicle.common"
        >
          <base-select
            id="vehicle_trailer_type"
            v-model="formData.trailer_type"
            :options="vehicleStore.trailerTypeOptions"
            placeholder="Выберите тип прицепа"
          />
        </BaseFormField>

        <BaseFormField
          label="Вид ТС для ЭДО"
          for-id="vehicle_edo_vehicle_type"
          :filled="Boolean(formData.edo_vehicle_type)"
          info-key="vehicle.common"
        >
          <base-select
            id="vehicle_edo_vehicle_type"
            v-model="formData.edo_vehicle_type"
            :options="edoEntityStore.vehicleTypeOptions"
            placeholder="Выберите вид ТС для Saby"
          />
        </BaseFormField>

        <BaseFormField
          v-if="formData.vehicle_type === 'tractor'"
          label="Прицеп"
          for-id="vehicle_trailer_id"
        >
          <TrailerSelect
            id="vehicle_trailer_id"
            v-model="formData.trailer_id"
            :exclude-ids="excludeTrailerIds"
          />
        </BaseFormField>

        <BaseFormField
          label="Модель"
          for-id="vehicle_model"
          info-key="vehicle.common"
        >
          <base-input
            id="vehicle_model"
            v-model="formData.model"
            type="text"
            class="form-input"
          />
        </BaseFormField>

        <BaseFormField label="Производитель" for-id="vehicle_manufacturer">
          <base-input
            id="vehicle_manufacturer"
            v-model="formData.manufacturer"
            type="text"
            class="form-input"
          />
        </BaseFormField>

        <BaseFormField
          label="Грузоподъёмность, т"
          for-id="vehicle_capacity_tons"
          :filled="Boolean(formData.capacity_tons)"
          info-key="vehicle.common"
        >
          <base-input
            id="vehicle_capacity_tons"
            v-model="formData.capacity_tons"
            type="big_number"
            class="form-input"
            placeholder="Например, 20"
          />
        </BaseFormField>

        <BaseFormField
          label="Вместимость, м³"
          for-id="vehicle_volume_m3"
          :filled="Boolean(formData.volume_m3)"
          info-key="vehicle.common"
        >
          <base-input
            id="vehicle_volume_m3"
            v-model="formData.volume_m3"
            type="big_number"
            class="form-input"
            placeholder="Например, 82"
          />
        </BaseFormField>

        <BaseFormField label="Пробег (км)" for-id="vehicle_odometer">
          <base-input
            id="vehicle_odometer"
            v-model="formData.odometer"
            type="big_number"
            class="form-input"
          />
        </BaseFormField>
      </BaseFormSection>
    </div>

    <!-- Пользовательские поля сохраняются вместе с быстрым/обычным созданием ТС. -->
    <requisite-create
      v-if="withRequisites"
      ref="requisiteCreateRef"
      :allowed-models="['Vehicle']"
      :requisitable-type="'Vehicle'"
      :form-id="formId"
    />
  </form>
</template>

<script>
import { useEdoEntityStore } from "@/stores/edoEntityStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { useVehicleStore } from "@/stores/vehicleStore";

import RequisiteCreate from "@/components/Requisite/RequisiteCreate.vue";
import FleetParkSelect from "@/components/Vehicle/FleetParkSelect.vue";
import TrailerSelect from "@/components/Vehicle/TrailerSelect.vue";

const blankFormData = () => ({
  vehicle_type: "",
  edo_vehicle_type: "",
  trailer_type: "",
  capacity_tons: "",
  volume_m3: "",
  registration_number: "",
  model: "",
  manufacturer: "",
  odometer: null,
  trailer_id: null,
  fleet_park_id: null,
});

export default {
  name: "VehicleForm",

  components: {
    RequisiteCreate,
    FleetParkSelect,
    TrailerSelect,
  },

  props: {
    formId: {
      type: String,
      required: true,
    },
    vehicleId: {
      type: [String, Number],
      default: null,
    },
    initialData: {
      type: Object,
      default: () => ({}),
    },
    excludeTrailerIds: {
      type: Array,
      default: () => [],
    },
    withRequisites: {
      type: Boolean,
      default: false,
    },
    successMessage: {
      type: String,
      default: "Данные успешно сохранены",
    },
  },

  emits: ["saved", "submitting-change"],

  data() {
    return {
      formData: this.buildFormData(this.initialData),
      errors: {
        vehicle_type: false,
        registration_number: false,
      },
      isSubmitting: false,
    };
  },

  computed: {
    notificationStore() {
      return useNotificationStore();
    },

    vehicleStore() {
      return useVehicleStore();
    },

    edoEntityStore() {
      return useEdoEntityStore();
    },
  },

  async created() {
    await this.edoEntityStore.fetchVehicleTypes();
  },

  methods: {
    submit() {
      return this.handleSubmit();
    },

    buildFormData(data) {
      return {
        ...blankFormData(),
        ...data,
      };
    },

    handleVehicleTypeInput(value) {
      this.errors.vehicle_type = false;
      this.formData.vehicle_type = value;

      if (value !== "tractor") {
        this.formData.trailer_id = null;
      }

      if (value !== "trailer") {
        this.formData.trailer_type = "";
      }
    },

    validateForm() {
      let isValid = true;

      this.errors = {
        vehicle_type: false,
        registration_number: false,
      };

      if (!String(this.formData.vehicle_type || "").trim()) {
        this.errors.vehicle_type = true;
        isValid = false;
      }

      if (!String(this.formData.registration_number || "").trim()) {
        this.errors.registration_number = true;
        isValid = false;
      }

      if (!isValid) {
        this.scrollToFirstError();
      }

      return isValid;
    },

    scrollToFirstError() {
      this.$nextTick(() => {
        const firstErrorElement = this.$el.querySelector(
          ".invalid, .base-form-field__error"
        );

        if (!firstErrorElement) return;

        const formGroup = firstErrorElement.closest(".form-group");
        (formGroup || firstErrorElement).scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    },

    vehiclePayload() {
      return {
        ...this.formData,
        odometer: String(this.formData.odometer),
        trailer_id:
          this.formData.vehicle_type === "tractor"
            ? this.formData.trailer_id
            : null,
      };
    },

    async handleSubmit() {
      if (!this.validateForm()) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля корректно"
        );
        return;
      }

      try {
        this.setSubmitting(true);

        const vehicle = this.vehicleId
          ? await this.vehicleStore.updateVehicle(
              this.vehicleId,
              this.vehiclePayload()
            )
          : await this.vehicleStore.createVehicle(this.vehiclePayload());

        if (this.withRequisites && this.$refs.requisiteCreateRef) {
          await this.$refs.requisiteCreateRef.createRequisites(
            vehicle.id
          );
        }

        this.notificationStore.showSuccess(this.successMessage);
        this.$emit("saved", vehicle);
      } finally {
        this.setSubmitting(false);
      }
    },

    setSubmitting(value) {
      this.isSubmitting = value;
      this.$emit("submitting-change", value);
    },
  },
};
</script>

<style scoped>
.vehicle-form__block {
  margin-bottom: var(--base-out-indent);
}
</style>
